import type { AIContentType } from '@/mocks/aiRadioWizard';

/* ═══════════════════════════════════════════════════════════════
   Growth Center — Mock Data
   ═══════════════════════════════════════════════════════════════ */

export type GrowthAlertType = 'growing_fast' | 'at_risk' | 'no_activity';

export interface GrowthAlert {
  id: string;
  type: GrowthAlertType;
  radioName: string;
  radioFrequency: string;
  radioCity: string;
  message: string;
  detail: string;
  metric: string;
  changePercent: number;
  since: string;
}

export interface RadioGrowthData {
  id: string;
  radioName: string;
  radioFrequency: string;
  radioCity: string;
  newFollowers: number;
  newListeners: number;
  newPrograms: number;
  newHosts: number;
  newSponsors: number;
  growthPercent: number;
  trend: 'up' | 'down' | 'stable';
  weeksActive: number;
}

export interface GrowthMetrics {
  totalNewFollowers: number;
  totalNewListeners: number;
  totalNewPrograms: number;
  totalNewHosts: number;
  totalNewSponsors: number;
  totalRadiosTracked: number;
  avgGrowthPercent: number;
  growingFastCount: number;
  atRiskCount: number;
  noActivityCount: number;
  peakGrowingRadio: string;
  peakGrowingCity: string;
}

export const growthMetrics: GrowthMetrics = {
  totalNewFollowers: 18420,
  totalNewListeners: 47200,
  totalNewPrograms: 38,
  totalNewHosts: 14,
  totalNewSponsors: 9,
  totalRadiosTracked: 68,
  avgGrowthPercent: 12.4,
  growingFastCount: 8,
  atRiskCount: 5,
  noActivityCount: 3,
  peakGrowingRadio: 'Élite Digital',
  peakGrowingCity: 'CDMX',
};

export const radioGrowthData: RadioGrowthData[] = [
  {
    id: 'rg01',
    radioName: 'Élite Digital',
    radioFrequency: '102.5 FM',
    radioCity: 'CDMX',
    newFollowers: 3420,
    newListeners: 8900,
    newPrograms: 6,
    newHosts: 3,
    newSponsors: 2,
    growthPercent: 28.5,
    trend: 'up',
    weeksActive: 14,
  },
  {
    id: 'rg02',
    radioName: 'Éxito FM',
    radioFrequency: '98.3 FM',
    radioCity: 'Medellín',
    newFollowers: 2890,
    newListeners: 7200,
    newPrograms: 5,
    newHosts: 2,
    newSponsors: 2,
    growthPercent: 22.1,
    trend: 'up',
    weeksActive: 12,
  },
  {
    id: 'rg03',
    radioName: 'Deportiva Total',
    radioFrequency: '105.7 FM',
    radioCity: 'Buenos Aires',
    newFollowers: 2610,
    newListeners: 6100,
    newPrograms: 4,
    newHosts: 2,
    newSponsors: 1,
    growthPercent: 18.3,
    trend: 'up',
    weeksActive: 10,
  },
  {
    id: 'rg04',
    radioName: 'Sabor Latino',
    radioFrequency: '94.1 FM',
    radioCity: 'Guadalajara',
    newFollowers: 1980,
    newListeners: 5400,
    newPrograms: 4,
    newHosts: 1,
    newSponsors: 1,
    growthPercent: 15.7,
    trend: 'up',
    weeksActive: 10,
  },
  {
    id: 'rg05',
    radioName: 'Onda Tropical',
    radioFrequency: '97.9 FM',
    radioCity: 'Mérida',
    newFollowers: 1720,
    newListeners: 4300,
    newPrograms: 3,
    newHosts: 1,
    newSponsors: 1,
    growthPercent: 13.2,
    trend: 'up',
    weeksActive: 8,
  },
  {
    id: 'rg06',
    radioName: 'Rock Nacional',
    radioFrequency: '100.3 FM',
    radioCity: 'San José',
    newFollowers: 1450,
    newListeners: 3800,
    newPrograms: 3,
    newHosts: 2,
    newSponsors: 1,
    growthPercent: 11.0,
    trend: 'up',
    weeksActive: 8,
  },
  {
    id: 'rg07',
    radioName: 'Jazz & Blues',
    radioFrequency: '91.5 FM',
    radioCity: 'Monterrey',
    newFollowers: 1280,
    newListeners: 3200,
    newPrograms: 2,
    newHosts: 1,
    newSponsors: 0,
    growthPercent: 8.4,
    trend: 'stable',
    weeksActive: 6,
  },
  {
    id: 'rg08',
    radioName: 'Noticias 24',
    radioFrequency: '103.1 FM',
    radioCity: 'San Salvador',
    newFollowers: 1150,
    newListeners: 2900,
    newPrograms: 3,
    newHosts: 1,
    newSponsors: 1,
    growthPercent: 7.9,
    trend: 'stable',
    weeksActive: 6,
  },
  {
    id: 'rg09',
    radioName: 'Fiesta Urbana',
    radioFrequency: '99.7 FM',
    radioCity: 'Madrid',
    newFollowers: 1020,
    newListeners: 2600,
    newPrograms: 2,
    newHosts: 0,
    newSponsors: 0,
    growthPercent: 6.2,
    trend: 'stable',
    weeksActive: 5,
  },
  {
    id: 'rg10',
    radioName: 'Retro Hits',
    radioFrequency: '95.4 FM',
    radioCity: 'Lima',
    newFollowers: 890,
    newListeners: 2100,
    newPrograms: 2,
    newHosts: 1,
    newSponsors: 0,
    growthPercent: 4.8,
    trend: 'stable',
    weeksActive: 4,
  },
  {
    id: 'rg11',
    radioName: 'Clásica FM',
    radioFrequency: '88.7 FM',
    radioCity: 'Bogotá',
    newFollowers: 620,
    newListeners: 1500,
    newPrograms: 1,
    newHosts: 0,
    newSponsors: 0,
    growthPercent: 2.1,
    trend: 'down',
    weeksActive: 8,
  },
  {
    id: 'rg12',
    radioName: 'Radio Antigua',
    radioFrequency: '93.2 FM',
    radioCity: 'Santiago',
    newFollowers: 490,
    newListeners: 1100,
    newPrograms: 1,
    newHosts: 0,
    newSponsors: 0,
    growthPercent: -1.5,
    trend: 'down',
    weeksActive: 10,
  },
  {
    id: 'rg13',
    radioName: 'Música Andina',
    radioFrequency: '96.8 FM',
    radioCity: 'Quito',
    newFollowers: 380,
    newListeners: 900,
    newPrograms: 1,
    newHosts: 0,
    newSponsors: 0,
    growthPercent: -3.2,
    trend: 'down',
    weeksActive: 6,
  },
  {
    id: 'rg14',
    radioName: 'Voces del Sur',
    radioFrequency: '90.4 FM',
    radioCity: 'Asunción',
    newFollowers: 210,
    newListeners: 600,
    newPrograms: 0,
    newHosts: 0,
    newSponsors: 0,
    growthPercent: -5.8,
    trend: 'down',
    weeksActive: 5,
  },
  {
    id: 'rg15',
    radioName: 'Estudio 54',
    radioFrequency: '104.9 FM',
    radioCity: 'La Paz',
    newFollowers: 80,
    newListeners: 300,
    newPrograms: 0,
    newHosts: 0,
    newSponsors: 0,
    growthPercent: -8.1,
    trend: 'down',
    weeksActive: 3,
  },
  {
    id: 'rg16',
    radioName: 'FM Horizonte',
    radioFrequency: '101.8 FM',
    radioCity: 'Caracas',
    newFollowers: 30,
    newListeners: 200,
    newPrograms: 0,
    newHosts: 0,
    newSponsors: 0,
    growthPercent: -12.0,
    trend: 'down',
    weeksActive: 3,
  },
];

export const growthAlerts: GrowthAlert[] = [
  {
    id: 'ga01',
    type: 'growing_fast',
    radioName: 'Élite Digital',
    radioFrequency: '102.5 FM',
    radioCity: 'CDMX',
    message: 'Crecimiento acelerado detectado',
    detail: 'La radio está creciendo 3.2x más rápido que el promedio de la red. Sumó 3,420 seguidores y 8,900 oyentes nuevos en las últimas 2 semanas.',
    metric: 'Crecimiento general',
    changePercent: 28.5,
    since: '2 semanas',
  },
  {
    id: 'ga02',
    type: 'growing_fast',
    radioName: 'Éxito FM',
    radioFrequency: '98.3 FM',
    radioCity: 'Medellín',
    message: 'Pico de oyentes nuevos',
    detail: 'Éxito FM duplicó su audiencia en 10 días. Se detectó un aumento del 156% en oyentes nuevos desde que lanzó su programa matutino.',
    metric: 'Oyentes nuevos',
    changePercent: 22.1,
    since: '10 días',
  },
  {
    id: 'ga03',
    type: 'growing_fast',
    radioName: 'Sabor Latino',
    radioFrequency: '94.1 FM',
    radioCity: 'Guadalajara',
    message: 'Expansión de programas',
    detail: 'Sabor Latino sumó 4 programas nuevos en 3 semanas, atrayendo 1,980 seguidores nuevos. La estrategia de contenido diversificado está funcionando.',
    metric: 'Programas nuevos',
    changePercent: 15.7,
    since: '3 semanas',
  },
  {
    id: 'ga04',
    type: 'growing_fast',
    radioName: 'Rock Nacional',
    radioFrequency: '100.3 FM',
    radioCity: 'San José',
    message: 'Engagement en alza',
    detail: 'Rock Nacional está viendo un incremento sostenido de interacciones. +1,450 seguidores y +2 nuevos locutores en 2 semanas.',
    metric: 'Engagement',
    changePercent: 11.0,
    since: '2 semanas',
  },
  {
    id: 'ga05',
    type: 'at_risk',
    radioName: 'Radio Antigua',
    radioFrequency: '93.2 FM',
    radioCity: 'Santiago',
    message: 'Descenso de audiencia',
    detail: 'Radio Antigua perdió un 18% de oyentes activos en el último mes. La frecuencia de programas bajó de 8 a 4 por semana.',
    metric: 'Oyentes activos',
    changePercent: -1.5,
    since: '1 mes',
  },
  {
    id: 'ga06',
    type: 'at_risk',
    radioName: 'Música Andina',
    radioFrequency: '96.8 FM',
    radioCity: 'Quito',
    message: 'Caída de seguidores',
    detail: 'Música Andina perdió 120 seguidores esta semana. Sin programas nuevos en 3 semanas. Se recomienda revisar la parrilla.',
    metric: 'Seguidores',
    changePercent: -3.2,
    since: '1 semana',
  },
  {
    id: 'ga07',
    type: 'at_risk',
    radioName: 'Clásica FM',
    radioFrequency: '88.7 FM',
    radioCity: 'Bogotá',
    message: 'Estancamiento de crecimiento',
    detail: 'Clásica FM mantiene métricas planas por 4 semanas consecutivas. Crecimiento de solo 2.1% con tendencia a la baja.',
    metric: 'Crecimiento general',
    changePercent: 2.1,
    since: '4 semanas',
  },
  {
    id: 'ga08',
    type: 'at_risk',
    radioName: 'Voces del Sur',
    radioFrequency: '90.4 FM',
    radioCity: 'Asunción',
    message: 'Baja interacción comunitaria',
    detail: 'Voces del Sur registra la menor tasa de interacción de la red. 0 patrocinadores nuevos y caída del 5.8% en crecimiento.',
    metric: 'Interacción',
    changePercent: -5.8,
    since: '2 semanas',
  },
  {
    id: 'ga09',
    type: 'no_activity',
    radioName: 'Estudio 54',
    radioFrequency: '104.9 FM',
    radioCity: 'La Paz',
    message: 'Sin actividad reciente',
    detail: 'Estudio 54 no registra programas nuevos, locutores nuevos ni actividad comunitaria en las últimas 3 semanas.',
    metric: 'Actividad general',
    changePercent: -8.1,
    since: '3 semanas',
  },
  {
    id: 'ga10',
    type: 'no_activity',
    radioName: 'FM Horizonte',
    radioFrequency: '101.8 FM',
    radioCity: 'Caracas',
    message: 'Radio inactiva',
    detail: 'FM Horizonte no tiene transmisiones registradas en 12 días. Sin actualizaciones de contenido ni actividad de locutores.',
    metric: 'Transmisiones',
    changePercent: -12.0,
    since: '12 días',
  },
];